import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  PARTY_ROLES,
  balanceOf,
  cycleSuffix,
  isSettled,
  reliability,
  type InvoiceStatus,
  type LeaseStatus,
  type RentCycle,
  termSuffix,
  toTerm,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation, formValues } from "@/lib/mutations";
import { telHref, titleCase, useFormat, whatsappHref } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useActiveModule, useModuleLink } from "@/lib/module-rail";
import { InvoiceStatusBadge, LeaseStatusBadge, MessageStatusBadge } from "@/components/status";
import { Alert, Badge, Button, Card, CardTitle, Detail, EmptyState, ErrorState, Field, Input, LinkButton, PageHeader, Spinner, Table } from "@/components/ui";
import { OptionMark, useOrgOptions } from "@/components/option-picker";

/**
 * The client record.
 *
 * Built to be read in the ten seconds before a phone call: what they owe, what they are renting,
 * when it ends, and — the part most systems omit — exactly what has already been sent to them,
 * in the words they received. An agent who cannot see the last reminder sends a fifth one.
 */

interface ClientResponse {
  party: Record<string, string | number | string[] | null>;
  leases: Array<{
    lease: { id: string; reference: string; status: string; startDate: string; endDate: string | null; rentAmount: number; rentCycle: string; rentCycleEvery?: number };
    unitLabel: string;
    propertyName: string;
  }>;
  invoices: Array<{ id: string; number: string; dueDate: string; total: number; paid: number; status: string; settledOn: string | null; unitLabel: string | null }>;
  payments: Array<{ id: string; receiptNumber: string; amount: number; receivedOn: string; method: string; status: string; unallocated: number }>;
  messages: Array<{
    id: string;
    channel: string;
    status: string;
    subject: string | null;
    body: string;
    origin: string;
    trigger: string | null;
    sentAt: string | null;
    createdAt: string;
    error: string | null;
  }>;
  ownedProperties: Array<{
    id: string;
    name: string;
    city: string | null;
    ownedUnits: number;
    totalUnits: number;
  }>;
  today: string;
  graceDays: number;
}

/**
 * Whether this client can sign in to the portal, and the repair when they cannot.
 *
 * **Why this panel exists at all.** Identity in the portal is a confirmed email address, and a
 * client is matched to their account by it. So an address the agency typed wrongly is a buyer who
 * registers, matches nothing, and telephones the office — and without somewhere to fix it that call
 * has no answer. ADR-0102 calls the manual link a product feature rather than an admin escape
 * hatch, and this is where it lives.
 *
 * **What is shown is deliberately thin.** Whether there is an account, its name and address, and
 * whether the address has been confirmed. Not the password, not the second factor, not when they
 * last signed in — an agency is looking at somebody else's credential, and the endpoint returns
 * only those three facts for exactly that reason.
 *
 * **It never creates an account.** The person registers themselves and the agency joins the two;
 * anything else would be an agency minting a credential in somebody else's name. The refusals — an
 * address nobody has registered, one that has not been confirmed, one that signs in to an agency —
 * come back from the API already naming the fix rather than only the rule, so they are shown as
 * they arrive.
 */
function PortalAccountPanel({ partyId, email }: { partyId: string; email: string | null }) {
  const { can } = useSession();
  const [linking, setLinking] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["party", partyId, "portal-account"],
    queryFn: () =>
      api.get<{ linked: boolean; account: { name: string; email: string; confirmed: boolean } | null }>(
        `/parties/${partyId}/portal-account`,
      ),
    enabled: Boolean(partyId),
  });

  const link = useApiMutation({
    mutationFn: (input: { email: string }) => api.post(`/parties/${partyId}/portal-account`, input),
    invalidates: [["party", partyId, "portal-account"]],
    onSuccess: () => setLinking(false),
  });

  const unlink = useApiMutation({
    mutationFn: () => api.delete(`/parties/${partyId}/portal-account`),
    invalidates: [["party", partyId, "portal-account"]],
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    link.mutate({ email: formValues(event.currentTarget).email });
  };

  return (
    <Card>
      <CardTitle hint="Where this client signs in to see their own tenancy or purchase.">Portal access</CardTitle>

      {isLoading ? (
        <Spinner size="sm" />
      ) : data?.linked && data.account ? (
        <>
          <dl>
            <Detail label="Account">{data.account.name}</Detail>
            <Detail label="Address">{data.account.email}</Detail>
            <Detail label="Confirmed">
              {data.account.confirmed ? <Badge tone="success">Confirmed</Badge> : <Badge tone="warn">Not yet</Badge>}
            </Detail>
          </dl>
          {/* An unconfirmed address matches no records at all, so it is worth saying what the client
              has to do rather than leaving a badge nobody can act on. */}
          {!data.account.confirmed ? (
            <p className="mt-3 text-sm text-ink-500">
              They still have to follow the link sent to that address. Until they do, signing in shows them nothing.
            </p>
          ) : null}
          {can("party.edit") ? (
            <>
              {unlink.errorMessage ? (
                <div className="mt-3">
                  <Alert tone="danger">{unlink.errorMessage}</Alert>
                </div>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                disabled={unlink.isPending}
                onClick={() => unlink.mutate(undefined)}
              >
                {unlink.isPending ? "Unlinking…" : "Unlink this account"}
              </Button>
            </>
          ) : null}
        </>
      ) : (
        <>
          <p className="text-sm text-ink-500">
            No account is linked yet. They can register at the portal with{" "}
            {email ? <span className="font-medium text-ink-700">{email}</span> : "the address on this record"} and will
            be matched to this record once they confirm it.
          </p>
          {can("party.edit") ? (
            linking ? (
              <form onSubmit={submit} className="mt-4 space-y-3">
                {link.errorMessage ? <Alert tone="danger">{link.errorMessage}</Alert> : null}
                <Field
                  label="The address they registered with"
                  htmlFor="portal-account-email"
                  required
                  hint="For when the address on this record was typed wrongly, so their own registration matched nothing."
                >
                  <Input
                    id="portal-account-email"
                    name="email"
                    type="email"
                    required
                    autoFocus
                    defaultValue={email ?? ""}
                    placeholder="them@example.com"
                  />
                </Field>
                <div className="flex items-center gap-3">
                  <Button type="submit" size="sm" disabled={link.isPending}>
                    {link.isPending ? "Linking…" : "Link it"}
                  </Button>
                  <button
                    type="button"
                    className="text-sm text-ink-500 hover:text-ink-800"
                    onClick={() => setLinking(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <Button variant="secondary" size="sm" className="mt-4" onClick={() => setLinking(true)}>
                Link an account they registered
              </Button>
            )
          ) : null}
        </>
      )}
    </Card>
  );
}

export function ClientDetailPage() {
  const { id } = useParams();
  const { vocab, can, modules } = useSession();
  /*
    **The client record is a screen both modules share, and half of it is letting's alone.**

    A buyer is opened from the development rail — the Buyers list links straight here — and the
    cards below carried a tenancy, an invoice, a landlord's properties and "send a reminder", every
    one of them an address `moduleForPath` gives to letting. Pressing one moved the whole
    navigation into the other business, which is what the operator reported. The records have not
    gone anywhere: the same client read from the letting rail shows all of them.
  */
  const active = useActiveModule(modules);
  const inLetting = active === "letting";
  const link = useModuleLink(modules);
  const fmt = useFormat();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["party", id],
    queryFn: () => api.get<ClientResponse>(`/parties/${id}`),
    enabled: Boolean(id),
  });

  /**
   * Above the guards, like every other hook.
   *
   * A hook that only runs once the data has arrived runs on some renders and not others, and
   * React refuses to render at all — the page came up blank with "rendered more hooks than during
   * the previous render". It belongs here even though it is only used far below.
   */
  const [pickingWhatsapp, setPickingWhatsapp] = useState(false);
  /* Above the guards for the same reason. The record carries the status id and nothing else; the
     name and the mark live on the agency's own list. */
  const { data: statuses } = useOrgOptions("client_status");

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const party = data.party;
  const roles = (party.roles as string[] | undefined) ?? [];
  const isTenant = roles.includes("tenant");
  const isLandlord = roles.includes("landlord");
  const status = statuses?.options.find((option) => option.id === party.statusOptionId) ?? null;

  const outstanding = data.invoices
    .filter((invoice) => !isSettled(invoice.status as InvoiceStatus))
    .reduce((sum, invoice) => sum + balanceOf({ total: invoice.total, paid: invoice.paid }), 0);
  const totalPaid = data.payments.filter((p) => p.status === "confirmed").reduce((sum, p) => sum + p.amount, 0);
  const credit = data.payments.reduce((sum, p) => sum + p.unallocated, 0);

  const score = reliability(
    data.invoices.map((invoice) => ({ dueDate: invoice.dueDate, settledOn: invoice.settledOn })),
    data.today,
    data.graceDays,
  );

  const phone = String(party.phone ?? "");
  const phones = ((party.phones as unknown as Array<{ number: string; whatsapp: boolean }>) ?? []).filter(
    (entry) => entry?.number,
  );
  /**
   * Which numbers can take a WhatsApp message.
   *
   * With one, the button goes straight there. With several, it asks — a message sent to the wrong
   * one of somebody's three numbers is a message they never see.
   */
  const whatsappable = phones.filter((entry) => entry.whatsapp).map((entry) => entry.number);

  return (
    <div>
      <PageHeader
        title={String(party.name)}
        back={{ to: link("/app/clients"), label: "Clients" }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {roles.map((role) => (
              <Badge key={role} tone={role === "tenant" ? "info" : role === "landlord" ? "success" : "muted"}>
                {PARTY_ROLES.find((r) => r.value === role)?.label ?? role}
              </Badge>
            ))}
            {phone ? (
              <a href={telHref(phone)} className="text-ink-500 hover:underline">
                {phone}
              </a>
            ) : null}
          </span>
        }
        actions={
          <>
            {/* The way in to editing was a small text link halfway down the right-hand column,
                which is not where anybody looks for it. It belongs with the other actions. */}
            {can("party.edit") ? (
              <LinkButton to={`/app/clients/${id}/edit`} variant="secondary" need="party.edit">
                Edit client
              </LinkButton>
            ) : null}
            {whatsappable.length === 1 ? (
              <a
                href={whatsappHref(whatsappable[0], `Hello ${String(party.name).split(" ")[0]},`)}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                WhatsApp
              </a>
            ) : whatsappable.length > 1 ? (
              <span className="relative">
                <Button variant="secondary" onClick={() => setPickingWhatsapp((open) => !open)}>
                  WhatsApp
                </Button>
                {pickingWhatsapp ? (
                  <span
                    className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-[var(--radius-surface)] border border-ink-200 bg-white py-1"
                    style={{ boxShadow: "var(--shadow-menu)" }}
                  >
                    {whatsappable.map((number) => (
                      <a
                        key={number}
                        href={whatsappHref(number, `Hello ${String(party.name).split(" ")[0]},`)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setPickingWhatsapp(false)}
                        className="block px-3 py-2 text-left text-sm text-ink-700 hover:bg-ink-50"
                      >
                        {number}
                      </a>
                    ))}
                  </span>
                ) : null}
              </span>
            ) : null}
            {can("reminder.send") && isTenant && inLetting ? (
              <LinkButton to={`/app/reminders/send?partyId=${id}`} variant="secondary" need="reminder.send">
                Send a reminder
              </LinkButton>
            ) : null}
            {can("payment.record") ? <LinkButton to={`/app/payments/new?partyId=${id}`} need="payment.record">Record a payment</LinkButton> : null}
          </>
        }
      />

      {isTenant && inLetting ? (
        <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-l-4 border-l-danger-500">
            <p className="text-xs uppercase tracking-wide text-ink-500">Outstanding</p>
            <p className="tabular mt-1 text-xl font-semibold">{fmt.money(outstanding)}</p>
          </Card>
          <Card className="border-l-4 border-l-brand-500">
            <p className="text-xs uppercase tracking-wide text-ink-500">Paid to date</p>
            <p className="tabular mt-1 text-xl font-semibold">{fmt.money(totalPaid)}</p>
          </Card>
          <Card className="border-l-4 border-l-info-600">
            <p className="text-xs uppercase tracking-wide text-ink-500">Credit on account</p>
            <p className="tabular mt-1 text-xl font-semibold">{fmt.money(credit)}</p>
          </Card>
          <Card className="border-l-4 border-l-ink-300">
            <p className="text-xs uppercase tracking-wide text-ink-500">Reliability</p>
            <p className="mt-1 text-xl font-semibold">{score.label}</p>
            <p className="text-xs text-ink-500">
              {score.onTime} on time · {score.late} late · {score.outstanding} outstanding
            </p>
          </Card>
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {isTenant && inLetting ? (
            <Card padded={false}>
              <div className="p-4 sm:p-5">
                <CardTitle
                  action={
                    can("lease.edit") ? (
                      <Link to={`/app/tenancies/new?tenantId=${id}`} className="link">
                        New {vocab.lease}
                      </Link>
                    ) : null
                  }
                >
                  {titleCase(vocab.leases)}
                </CardTitle>
              </div>
              {data.leases.length === 0 ? (
                <EmptyState
                  title={`No ${vocab.leases} yet.`}
                  action={
                    can("lease.edit") ? (
                      <LinkButton to={`/app/tenancies/new?tenantId=${id}`} need="lease.edit">Create {titleCase(vocab.lease)}</LinkButton>
                    ) : undefined
                  }
                />
              ) : (
                <Table
                  head={
                    <tr>
                      <th>Reference</th>
                      <th>{titleCase(vocab.unit)}</th>
                      <th>Term</th>
                      <th>Status</th>
                      <th className="num">{titleCase(vocab.rent)}</th>
                    </tr>
                  }
                >
                  {data.leases.map((row) => (
                    <tr key={row.lease.id}>
                      <td>
                        <Link to={`/app/tenancies/${row.lease.id}`} className="link">
                          {row.lease.reference}
                        </Link>
                      </td>
                      <td>
                        {row.unitLabel}
                        <span className="block text-xs text-ink-400">{row.propertyName}</span>
                      </td>
                      <td className="text-ink-500">
                        {fmt.date(row.lease.startDate)} → {fmt.date(row.lease.endDate)}
                      </td>
                      <td>
                        <LeaseStatusBadge
                          lease={{
                            status: row.lease.status as LeaseStatus,
                            startDate: row.lease.startDate,
                            endDate: row.lease.endDate,
                          }}
                          asOf={data.today}
                        />
                      </td>
                      <td className="num tabular">
                        {fmt.money(row.lease.rentAmount)}
                        <span className="block text-xs text-ink-400">{termSuffix(toTerm(row.lease.rentCycle as RentCycle, row.lease.rentCycleEvery))}</span>
                      </td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          ) : null}

          {isLandlord && inLetting ? (
            <Card padded={false}>
              <div className="p-4 sm:p-5">
                <CardTitle action={<Link to={`/app/reports/statement/${id}`} className="link">Statement</Link>}>
                  {titleCase(vocab.properties)} owned
                </CardTitle>
              </div>
              {data.ownedProperties.length === 0 ? (
                <EmptyState title={`No ${vocab.properties} linked to this ${vocab.landlord}.`} />
              ) : (
                <Table head={<tr><th>Name</th><th>Where</th><th className="num">{titleCase(vocab.units)}</th></tr>}>
                  {data.ownedProperties.map((property) => (
                    <tr key={property.id}>
                      <td>
                        <Link to={`/app/properties/${property.id}`} className="link">
                          {property.name}
                        </Link>
                      </td>
                      <td className="text-ink-500">{property.city ?? "—"}</td>
                      {/* Owning six flats in a twenty-flat block is not owning the block, and
                          the count is the only honest way to say so on one line. */}
                      <td className="num tabular text-ink-500">
                        {property.totalUnits && property.ownedUnits < property.totalUnits
                          ? `${property.ownedUnits} of ${property.totalUnits}`
                          : property.ownedUnits || "—"}
                      </td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          ) : null}

          {/* An invoice is a letting record — `/invoices` is behind `requireModule("letting")` on
              the server — so the card, and the link on every row of it, belongs to the letting
              rail. A buyer's money is on their purchase, which is where the development rail shows
              it. */}
          {inLetting ? (
          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle>{titleCase(vocab.invoices)}</CardTitle>
            </div>
            {data.invoices.length === 0 ? (
              <EmptyState title={`No ${vocab.invoices} raised.`} />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Number</th>
                    <th>{titleCase(vocab.unit)}</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th className="num">Total</th>
                    <th className="num">Balance</th>
                  </tr>
                }
              >
                {data.invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <Link to={`/app/invoices/${invoice.id}`} className="link">
                        {invoice.number}
                      </Link>
                    </td>
                    <td className="text-ink-500">{invoice.unitLabel ?? "—"}</td>
                    <td className="text-ink-500">{fmt.date(invoice.dueDate)}</td>
                    <td>
                      <InvoiceStatusBadge
                        invoice={{ dueDate: invoice.dueDate, total: invoice.total, paid: invoice.paid, status: invoice.status as InvoiceStatus }}
                        asOf={data.today}
                        graceDays={data.graceDays}
                      />
                    </td>
                    <td className="num tabular">{fmt.money(invoice.total)}</td>
                    <td className="num tabular font-medium">{fmt.money(invoice.total - invoice.paid)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
          ) : null}

          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle hint="Every reminder sent, in the words they received it.">Message history</CardTitle>
            </div>
            {data.messages.length === 0 ? (
              <EmptyState title="Nothing has been sent to this client yet." />
            ) : (
              <ul className="divide-y divide-ink-100">
                {data.messages.slice(0, 12).map((message) => (
                  <li key={message.id} className="px-4 py-3 sm:px-5">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <MessageStatusBadge status={message.status} />
                      <span className="text-ink-500">{message.channel}</span>
                      <span className="text-ink-400">·</span>
                      <span className="text-ink-500">{message.origin === "auto" ? "Automatic" : "Sent by hand"}</span>
                      <span className="ml-auto text-ink-400">{fmt.dateTime(message.sentAt ?? message.createdAt)}</span>
                    </div>
                    <p className="mt-1 text-sm font-medium">{message.subject ?? message.trigger ?? "Message"}</p>
                    <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-ink-500">{message.body}</p>
                    {message.error ? <p className="mt-1 text-xs text-danger-600">{message.error}</p> : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardTitle
              action={
                can("party.edit") ? (
                  <LinkButton to={`/app/clients/${id}/edit`} variant="secondary" size="sm" need="party.edit">
                    Edit details
                  </LinkButton>
                ) : null
              }
            >
              Contact
            </CardTitle>
            <dl>
              {/* Every number they gave, in the order they gave them. The first is the one we ring. */}
              {phones.length ? (
                phones.map((entry, index) => (
                  <Detail key={`${entry.number}-${index}`} label={index === 0 ? "Phone" : "Also"}>
                    <span className="inline-flex items-center gap-2">
                      <a href={telHref(entry.number)} className="hover:underline">
                        {entry.number}
                      </a>
                      {entry.whatsapp ? (
                        <span className="rounded-[var(--radius-control)] bg-brand-50 px-1.5 py-0.5 text-[11px] font-medium text-brand-800">
                          WhatsApp
                        </span>
                      ) : null}
                    </span>
                  </Detail>
                ))
              ) : (
                <Detail label="Phone">—</Detail>
              )}
              <Detail label="Email">{String(party.email ?? "—")}</Detail>
              <Detail label="Address">{String(party.address ?? "—")}</Detail>
              <Detail label="Occupation">{String(party.occupation ?? "—")}</Detail>
              <Detail label="Employer">{String(party.employer ?? "—")}</Detail>
              <Detail label="ID">{party.idNumber ? `${party.idType ?? "ID"} · ${party.idNumber}` : "—"}</Detail>
              <Detail label="Next of kin">{String(party.nextOfKin ?? "—")}</Detail>
              {/*
                A commission rate was here and is gone with the model that produced it: nothing is
                taken off a landlord's rent, so there is no rate to state. ADR-0131.

                Where this relationship stands takes its place — a fact about the client rather
                than about their money, which is why it sits with their contact details. Drawn only
                where one has been set: a status nobody chose is nothing, and "Not set" on every
                record would be a column of the same three words.
              */}
              {status ? (
                <Detail label="Status">
                  <OptionMark name={status.name} tone={status.tone} />
                </Detail>
              ) : null}
            </dl>
            {party.notes ? <p className="mt-3 whitespace-pre-wrap text-sm text-ink-600">{String(party.notes)}</p> : null}
          </Card>

          {/* Beneath the contact details, because it is a fact about this person's address rather
              than about their money. */}
          {id ? <PortalAccountPanel partyId={id} email={party.email ? String(party.email) : null} /> : null}

          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle>Payments</CardTitle>
            </div>
            {data.payments.length === 0 ? (
              <EmptyState title="No payments recorded." />
            ) : (
              <ul className="divide-y divide-ink-100">
                {data.payments.slice(0, 8).map((payment) => (
                  <li key={payment.id} className="flex items-center justify-between px-4 py-2.5 text-sm sm:px-5">
                    <span>
                      <Link to={`/app/payments/${payment.id}`} className="link">
                        {payment.receiptNumber}
                      </Link>
                      <span className="block text-xs text-ink-500">
                        {fmt.date(payment.receivedOn)} · {payment.method.replace(/_/g, " ")}
                      </span>
                    </span>
                    <span className="tabular font-medium">{fmt.money(payment.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
